import { Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { combineLatest, Observable, of } from 'rxjs';
import { debounceTime, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { EntitiesService } from 'src/app/services/entities/entities.service';
import { Entity } from '../../../../classes/entity.class';
import { Hiring } from '../../../../classes/hiring';
import { HiringService } from '../../../../services/hiring.service';
import { InputComponent } from '../input/input.component';

@Component({
    selector: 'app-entity-input',
    standalone: false,
    templateUrl: './entity-input.component.html',
    styleUrl: './entity-input.component.scss',
})
export class EntityInputComponent extends InputComponent implements OnInit {
    private entitiesService = inject(EntitiesService);
    private hiringService = inject(HiringService);

    entities$ = combineLatest([
        this.fc.parent?.get('begindate')?.valueChanges.pipe(startWith(this.fc.parent?.get('begindate')?.value)) ??
            of(undefined),
        this.fc.parent?.get('enddate')?.valueChanges.pipe(startWith(this.fc.parent?.get('enddate')?.value)) ??
            of(undefined),
        this.fc.valueChanges.pipe(startWith(this.fc.value)),
    ]).pipe(
        debounceTime(0),
        switchMap(([begindate, enddate, entityId]) =>
            this.entitiesService.getByType(
                this.field.dataFilter,
                { begindate, enddate },
                [entityId].filter(id => !!id)
            )
        ),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    availableEntities$: Observable<Entity[]>;
    hiring$: Observable<Hiring[]>;

    fcHiringId: FormControl<number>;
    fcName: FormControl<string>;

    entity: WritableSignal<Entity> = signal(null);

    private get fieldKey(): string {
        return Object.entries(this.fc.parent?.controls ?? {}).find(([, control]) => control === this.fc)?.[0];
    }

    ngOnInit() {
        const value$ = this.fc.valueChanges;
        const planningHas = this.formData.form.controls.planningHas;
        const fieldKey = this.fieldKey;

        this.availableEntities$ = combineLatest([
            this.entities$,
            planningHas.valueChanges.pipe(startWith(planningHas.value)),
            value$.pipe(startWith(this.fc.value)),
        ]).pipe(
            map(([entities, planningHasValues, currentValue]) => {
                const usedEntityIds = planningHasValues
                    .map(planningHasValue => planningHasValue.phe?.[fieldKey])
                    .filter(entityId => !!entityId && entityId !== currentValue);

                return entities.filter(entity => !usedEntityIds.includes(entity.id) || !entity.use_once);
            })
        );

        this.subscriptions.add(
            combineLatest([value$, this.entities$]).subscribe(([entityId, entities]) => {
                const entity = entities.find(e => e.id === entityId);

                if (this.fc.parent.value?.entity_id !== entityId && !this.fc?.parent?.get('driver_user_id')?.value) {
                    if (entity?.driver_user_id) {
                        this.formData.form.controls.planningHas.controls
                            .filter(c => c.controls.phe.get('entity_id').value === entityId)
                            .forEach(c => {
                                c.controls.phe.get('driver_user_id').setValue(entity.driver_user_id);
                            });
                    }
                }

                // truck_entity_id/lowloader_entity_id are the entity's own defaults, only cascade
                // them when it's the main entity_id field (not this component's other instances)
                if (this.fc.parent?.get('entity_id') === this.fc) {
                    this.formData.form.controls.planningHas.controls
                        .filter(c => c.controls.phe.get('entity_id').value === entityId)
                        .forEach(c => {
                            const truckControl = c.controls.phe.get('truck_entity_id');

                            if (entity?.truck_entity_id && truckControl && !truckControl.value) {
                                truckControl.setValue(entity.truck_entity_id);
                            }

                            const lowloaderControl = c.controls.phe.get('lowloader_entity_id');

                            if (entity?.lowloader_entity_id && lowloaderControl && !lowloaderControl.value) {
                                lowloaderControl.setValue(entity.lowloader_entity_id);
                            }
                        });
                }

                if (entity?.entitytypes?.length && !entity.use_once) {
                    this.hiring$ = combineLatest(
                        entity.entitytypes.map(et => this.hiringService.getByType(et.key))
                    ).pipe(
                        map(results =>
                            results.flat().filter((h, index, self) => self.findIndex(x => x.id === h.id) === index)
                        )
                    );
                }

                this.entity.set(entity);
            })
        );

        if (this.fc.parent.get('hiring_id')) {
            this.fcHiringId = this.fc.parent.get('hiring_id') as FormControl<number>;
        }

        if (this.fc.parent.get('name')) {
            this.fcName = this.fc.parent.get('name') as FormControl<string>;
        }
    }
}
